import { UserSchema } from "@/api/user/user";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api, isApiError } from "..";
import { UploadedFileMetadataSchema } from "../uploaded_file/uploaded_file";
import { zodParse } from "../zodParse";
import _ from "lodash";

// ============================================================================
// ENUMS & CONSTANTS
// ============================================================================

export const ApplicantType = {
  SELF: "self",
  PROXY: "proxy",
} as const;

export const VisionCondition = {
  AGE_RELATED_MACULAR_DEGENERATION: "AGE_RELATED_MACULAR_DEGENERATION",
  RETINITIS_PIGMENTOSA: "RETINITIS_PIGMENTOSA",
  STARGARDTS_DISEASE: "STARGARDTS_DISEASE",
} as const;

export const VisionConditionLabels: Record<keyof typeof VisionCondition, string> = {
  AGE_RELATED_MACULAR_DEGENERATION: "Age-related macular degeneration",
  RETINITIS_PIGMENTOSA: "Retinitis Pigmentosa",
  STARGARDTS_DISEASE: "Stargardt Disease",
};

export const allVisionConditions = {
  AGE_RELATED_MACULAR_DEGENERATION: "AGE_RELATED_MACULAR_DEGENERATION",
  RETINITIS_PIGMENTOSA: "RETINITIS_PIGMENTOSA",
  STARGARDTS_DISEASE: "STARGARDTS_DISEASE",
  GLAUCOMA: "GLAUCOMA",
  DIABETIC_RETINOPATHY: "DIABETIC_RETINOPATHY",
  CHOROIDEREMIA: "CHOROIDEREMIA",
  RETINAL_DETACHMENT: "RETINAL_DETACHMENT",
  ACCIDENT_TRAUMAT: "ACCIDENT_TRAUMAT",
  UNKNOWN: "UNKNOWN",
  OTHER: "OTHER",
} as const;

export const allVisionConditionLabels: Record<keyof typeof allVisionConditions, string> = {
  AGE_RELATED_MACULAR_DEGENERATION: "Age-related macular degeneration",
  RETINITIS_PIGMENTOSA: "Retinitis Pigmentosa",
  STARGARDTS_DISEASE: "Stargardt Disease",
  GLAUCOMA: "Glaucoma",
  DIABETIC_RETINOPATHY: "Diabetic Retinopathy",
  CHOROIDEREMIA: "Choroideremia",
  RETINAL_DETACHMENT: "Retinal Detachment",
  ACCIDENT_TRAUMAT: "Accident / trauma",
  UNKNOWN: "Unknown / I don't know",
  OTHER: "Other...",
};

// ============================================================================
// SCHEMAS
// ============================================================================

/**
 * Defines the shape of the Application data being edited.
 * All fields are optional to support saving drafts.
 */
export const ApplicationSchema = z.object({
  // Core
  id: z.string().uuid(),
  user_id: UserSchema.shape.id,
  is_submitted: z.boolean(),
  submitted_at: z.string().datetime().nullable(),
  updated_at: z.string().datetime().nullable(),
  status: z.enum(["pending", "approved", "rejected"]),
  applicant_type: z.nativeEnum(ApplicantType).nullable(),
  consent_locked: z.boolean(),
  consent_health_collection: z.boolean().nullable(),
  consent_health_sharing: z.boolean().nullable(),
  consent_contact: z.boolean().nullable(),
  consent_contact_email: z.boolean().nullable(),
  consent_contact_phone: z.boolean().nullable(),
  consent_contact_recurring: z.boolean().nullable(),
  patient_date_of_birth: z.string().nullable(),
  proxy_country: z.string().nullable(),
  proxy_state: z.string().nullable(),
  proxy_date_of_birth: z.string().nullable(),

  personal_patient_email: z.string().nullable(),
  personal_no_patient_email: z.boolean().nullable(),
  proxy_first_name: z.string().nullable(),
  proxy_last_name: z.string().nullable(),
  proxy_phone: z.string().nullable(),
  patient_first_name: z.string().nullable(),
  patient_last_name: z.string().nullable(),
  patient_phone: z.string().nullable(),
  patient_country: z.string().nullable(),
  patient_state: z.string().nullable(),

  // Vision Section
  vision_conditions: z.array(z.string()),
  vision_macular_degeneration_type: z.string().nullable(),
  vision_other_details: z.string().nullable(),
  vision_acuity_known: z.boolean().nullable(),
  vision_acuity_left: z.string().nullable(),
  vision_acuity_right: z.string().nullable(),
  vision_can_read_normal_print: z.boolean().nullable(),
  vision_can_read_headlines: z.boolean().nullable(),
  vision_can_see_indoors: z.boolean().nullable(),
  vision_can_localize_person: z.boolean().nullable(),
  vision_driving: z.string().nullable(),

  // Personal Section
  personal_country_of_residence: z.string().nullable(),
  personal_travel_willing: z.boolean().nullable(),
  applicant_is_adult: z.boolean().nullable(),

  // Free-text "anything else we should know?" from the confirm step
  additional_info: z.string().nullable(),
});

export const ApplicationFormSchema = ApplicationSchema.omit({
  id: true,
  user_id: true,
  status: true,
  updated_at: true,
}).extend({
  id: z.string().uuid().optional(),
  user_id: z.string().uuid().optional(),
});

export const UserFormSchema = UserSchema.pick({
  first_name: true,
  last_name: true,
});

/**
 * Schema for API error responses.
 */
export const ApiErrorSchema = z.object({
  message: z.string(),
  errors: z.record(z.array(z.string())).optional(), // For field-specific errors
});

// ============================================================================
// TYPES
// ============================================================================
export type UserFormData = z.infer<typeof UserFormSchema>;

export type Application = z.infer<typeof ApplicationSchema>;
export type ApplicationFormData = z.infer<typeof ApplicationFormSchema>;

export type ApplicationUserFormData = z.infer<typeof ApplicationFormSchema>;
export type ApiError = z.infer<typeof ApiErrorSchema>;

// ============================================================================
// API METHODS & HOOKS
// ============================================================================

export const currentApplicationQueryKey = ["application", "current"];

/**
 * Fetches the current user's application from the backend.
 * Returns null if the user has no application.
 */
async function fetchCurrentApplication(): Promise<Application | null> {
  try {
    const endpoint = `/registry/applications/current`;
    const { data: responseData } = await api.get(endpoint);

    // If the backend returns null, the user has no application
    if (responseData === null) {
      return null;
    }

    return zodParse(ApplicationSchema, responseData);
  } catch (error) {
    if (isApiError(error) && error.response) {
      const parsedError = ApiErrorSchema.safeParse(error.response.data);
      if (parsedError.success) {
        throw new Error(parsedError.data.message);
      }
    }
    throw new Error("An unexpected error occurred while fetching the application.");
  }
}

/**
 * A React Query hook for fetching the current user's application.
 * Provides query state (isLoading, isError, data, etc.).
 * Returns null if the user has no application.
 * Optimized to only fetch once on page load and prevent unnecessary refetches.
 */
export function useApplication() {
  return useQuery({
    queryKey: currentApplicationQueryKey,
    queryFn: () => fetchCurrentApplication(),
  });
}

type CreateApplicationPayload = Omit<ApplicationFormData, "id"> & { id?: never };
async function createApplication(formData: CreateApplicationPayload): Promise<Application> {
  try {
    const { data } = await api.post(`/registry/applications`, _.omit(formData, "consent_locked"));
    return zodParse(ApplicationSchema, data);
  } catch (error) {
    if (isApiError(error) && error.response) {
      const parsedError = ApiErrorSchema.safeParse(error.response.data);
      if (parsedError.success) {
        throw new Error(parsedError.data.message);
      }
    }
    throw new Error("An unexpected error occurred while creating the application.");
  }
}

export function useCreateApplication() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: ApplicationFormData) => {
      const { id, ...payload } = formData;
      const result = await createApplication(payload);
      queryClient.setQueryData(currentApplicationQueryKey, result);

      return result;
    },
  });
}

type UpdateApplicationPayload = Omit<ApplicationFormData, "id">;
async function updateApplication(
  formId: Application["id"],
  formData: UpdateApplicationPayload,
): Promise<Application> {
  try {
    const { data } = await api.put(
      `/registry/applications/${formId}`,
      _.omit(formData, "consent_locked"),
    );
    return zodParse(ApplicationSchema, data);
  } catch (error) {
    if (isApiError(error) && error.response) {
      const parsedError = ApiErrorSchema.safeParse(error.response.data);
      if (parsedError.success) {
        throw new Error(parsedError.data.message);
      }
    }
    throw new Error("An unexpected error occurred while updating the application.");
  }
}

export function useUpdateApplication() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: ApplicationFormData & { id: Application["id"] }) => {
      const { id, ...payload } = formData;
      const result = await updateApplication(id, payload);
      queryClient.setQueryData(currentApplicationQueryKey, result);

      return result;
    },
  });
}
